import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator, Modal } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { AdminToolbar } from '../../../src/components/AdminList';
import EditShiftModal, { ShiftLike } from '../../../src/components/EditShiftModal';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { useModalBottomPadding } from '../../../src/utils/useModalBottomPadding';
import { titleCase } from '../../../src/utils/format';

interface Attendant {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone?: string;
  isActive: boolean;
  locations: {
    locationId: string;
    locationName: string;
    role: string;
    roleLabel?: string;
  }[];
}

interface ShiftItem {
  id: string;
  attendantId: string;
  attendantName: string;
  locationId: string;
  locationName: string;
  clockInTime: string;
  clockOutTime?: string;
  status: string;
  notes?: string;
  durationMinutes: number;
  lockedAt?: string | null;
  lockedPeriodLabel?: string | null;
  selfCorrectedAt?: string | null;
  edited?: boolean;
}

interface RefundRequest {
  id: string;
  attendantName: string;
  amount: number;
  reason: string;
  type: string;
  status: string;
  createdAt: string;
}

interface RecentRefund {
  id: string;
  amount: number;
  reason: string;
  status: string;
  createdAt: string;
  refundedByName: string | null;
  customerName: string;
  paymentLast4: string | null;
}

/** StaffDashboardScreen - attendant list and shift history table. */
export default function StaffDashboardScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();

  const [attendants, setAttendants] = useState<Attendant[]>([]);
  const [loading, setLoading] = useState(true);
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [processingRefund, setProcessingRefund] = useState<string | null>(null);
  const [recentRefunds, setRecentRefunds] = useState<RecentRefund[]>([]);
  const [refundsTotal, setRefundsTotal] = useState(0);
  const [refundsPage, setRefundsPage] = useState(1);
  const [refundsSearch, setRefundsSearch] = useState('');
  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();

  // Shifts state
  const [shifts, setShifts] = useState<ShiftItem[]>([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsTotal, setShiftsTotal] = useState(0);
  const [shiftsPage, setShiftsPage] = useState(1);
  const [shiftsSearch, setShiftsSearch] = useState('');
  const [shiftsSort, setShiftsSort] = useState('clockInTime');
  const [shiftsSortOrder, setShiftsSortOrder] = useState<'asc' | 'desc'>('desc');
  const [shiftsStatusFilter, setShiftsStatusFilter] = useState('');
  const [selectedShift, setSelectedShift] = useState<ShiftItem | null>(null);
  const [editingShift, setEditingShift] = useState<ShiftLike | null>(null);

  const fetchAttendants = async () => {
    try {
      const data = await scope.api.get<Attendant[]>('/api/attendants');
      setAttendants(data);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchRefundRequests = async () => {
    try {
      const data = await scope.api.get<RefundRequest[]>('/api/owner/refund-requests');
      setRefundRequests(data.filter(r => r.status === 'pending'));
    } catch { /* ignore if endpoint not ready */ }
  };

  const fetchRecentRefunds = async (p = refundsPage, searchOverride?: string) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      const s = searchOverride ?? refundsSearch;
      if (s) params.set('search', s);
      const data = await scope.api.get<{ refunds: RecentRefund[]; total: number }>(`/api/owner/recent-refunds?${params}`);
      setRecentRefunds(data.refunds);
      setRefundsTotal(data.total);
      setRefundsPage(p);
    } catch { /* ignore */ }
  };

  const handleRefundAction = async (requestId: string, action: 'approve' | 'deny') => {
    setProcessingRefund(requestId);
    try {
      await scope.api.patch(`/api/owner/refund-requests/${requestId}`, { action });
      toast.show({ type: 'success', title: action === 'approve' ? 'Approved' : 'Denied', message: `Refund request ${action}d` });
      fetchRefundRequests();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setProcessingRefund(null);
    }
  };

  const fetchShifts = async (p = shiftsPage, signal?: AbortSignal) => {
    setShiftsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: '10',
        sort: shiftsSort,
        order: shiftsSortOrder,
      });
      if (shiftsSearch) params.set('search', shiftsSearch);
      if (shiftsStatusFilter) params.set('status', shiftsStatusFilter);
      const data = await scope.api.get<{ shifts: ShiftItem[]; total: number }>(`/api/attendants/owner-shifts?${params}`);
      if (signal?.aborted) return;
      setShifts(data.shifts);
      setShiftsTotal(data.total);
      setShiftsPage(p);
    } catch (err: any) {
      if (!(signal?.aborted)) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      if (!(signal?.aborted)) setShiftsLoading(false);
    }
  };

  useFocusEffect(useCallback(() => {
    fetchAttendants().then(() => fetchShifts(1));
    fetchRefundRequests();
    fetchRecentRefunds(1);
  }, []));

  useEffect(() => { const ac = new AbortController(); fetchShifts(1, ac.signal); return () => ac.abort(); }, [shiftsSort, shiftsSortOrder, shiftsStatusFilter]);

  const handleSortToggle = (col: string) => {
    if (shiftsSort === col) {
      setShiftsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setShiftsSort(col);
      setShiftsSortOrder('desc');
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatLocationSubtitle = (locs: Attendant['locations']) => {
    if (locs.length === 0) return 'No locations';
    return locs.map(l => {
      const label = l.roleLabel || (l.role ? l.role.charAt(0).toUpperCase() + l.role.slice(1) : '');
      return label ? `${l.locationName} (${label})` : l.locationName;
    }).join(', ');
  };

  return (
    <View style={styles.container}>
      <ScrollView
        refreshControl={<RefreshControl refreshing={loading} onRefresh={() => { fetchAttendants(); fetchShifts(1); }} />}
        contentContainerStyle={styles.scrollContent}
      >
        <ContentContainer>
        <Text style={styles.title}>Staff Management</Text>
        <View style={styles.headerButtons}>
          <Button
            title="Add Attendant"
            onPress={() => router.push(scope.basePath + '/staff/add' as never)}
            icon={<Ionicons name="person-add" size={18} color={colors.white} />}
            size="sm"
          />
          <Button
            title="Manage Roles"
            onPress={() => router.push(scope.basePath + '/staff/roles' as never)}
            icon={<Ionicons name="shield-checkmark" size={18} color={colors.primary} />}
            size="sm"
            variant="outline"
          />
          <Button
            title="QR Templates"
            onPress={() => router.push(scope.basePath + '/staff/reward-templates' as never)}
            icon={<Ionicons name="qr-code-outline" size={18} color={colors.primary} />}
            size="sm"
            variant="outline"
          />
          <Button
            title="Payroll"
            onPress={() => router.push(scope.basePath + '/payroll' as never)}
            icon={<Ionicons name="document-text" size={18} color={colors.primary} />}
            size="sm"
            variant="outline"
          />
        </View>

        {loading && attendants.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : attendants.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Ionicons name="people-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyTitle}>No Staff Members</Text>
            <Text style={styles.emptyText}>
              Add attendants to manage your car wash locations
            </Text>
            <Button
              title="Add First Attendant"
              onPress={() => router.push(scope.basePath + '/staff/add' as never)}
              style={styles.emptyButton}
            />
          </Card>
        ) : (
          <Card style={styles.attendantListCard}>
            {attendants.map((attendant, idx) => (
              <TouchableOpacity
                key={attendant.id}
                onPress={() => router.push(`${scope.basePath}/staff/${attendant.id}` as never)}
                style={[styles.attendantRow, idx < attendants.length - 1 && styles.attendantRowBorder]}
              >
                <View style={styles.attendantLeft}>
                  <Text style={styles.attendantName}>{titleCase(attendant.userName)}</Text>
                  <Text style={styles.attendantLocations} numberOfLines={1}>
                    {formatLocationSubtitle(attendant.locations)}
                  </Text>
                </View>
                <View style={styles.attendantRight}>
                  <View style={[styles.statusBadge, attendant.isActive ? styles.statusActive : styles.statusInactive]}>
                    <Text style={[styles.statusText, attendant.isActive ? styles.statusTextActive : styles.statusTextInactive]}>
                      {attendant.isActive ? 'Active' : 'Inactive'}
                    </Text>
                  </View>
                  <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
                </View>
              </TouchableOpacity>
            ))}
          </Card>
        )}

        {/* Pending Refund Requests */}
        {refundRequests.length > 0 && (
          <Card style={styles.shiftsCard}>
            <View style={styles.refundHeader}>
              <Text style={styles.sectionTitle}>Pending Refund Requests</Text>
              <View style={styles.refundBadge}>
                <Text style={styles.refundBadgeText}>{refundRequests.length}</Text>
              </View>
            </View>
            {refundRequests.map(req => (
              <View key={req.id} style={styles.refundRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.attendantName}>{titleCase(req.attendantName)}</Text>
                  <Text style={styles.attendantLocations}>{req.reason}</Text>
                  <Text style={styles.attendantLocations}>${(req.amount / 100).toFixed(2)} — {req.type}</Text>
                </View>
                <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                  <Button
                    title="Approve"
                    onPress={() => handleRefundAction(req.id, 'approve')}
                    size="sm"
                    loading={processingRefund === req.id}
                    style={{ minWidth: 80 }}
                  />
                  <Button
                    title="Deny"
                    onPress={() => handleRefundAction(req.id, 'deny')}
                    size="sm"
                    variant="danger"
                    loading={processingRefund === req.id}
                    style={{ minWidth: 60 }}
                  />
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Recent Refunds by Attendants */}
        {(recentRefunds.length > 0 || refundsTotal > 0 || refundsSearch !== '') && (
          <Card style={styles.shiftsCard}>
            <Text style={styles.sectionTitle}>Recent Refunds</Text>
            <AdminToolbar
              search={refundsSearch}
              onSearchChange={setRefundsSearch}
              onSearch={() => fetchRecentRefunds(1)}
              total={refundsTotal}
              page={refundsPage}
              limit={10}
              onPageChange={(p) => fetchRecentRefunds(p)}
              csvEndpoint={`/api/owner/recent-refunds${refundsSearch ? `?search=${encodeURIComponent(refundsSearch)}` : ''}`}
              csvFilename="refunds.csv"
              label="refund"
            />
            {recentRefunds.length === 0 && (
              <Text style={styles.shiftsEmptyText}>No refunds found</Text>
            )}
            {recentRefunds.map(r => (
              <View key={r.id} style={styles.refundRow}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.attendantName}>{titleCase(r.customerName)}{r.paymentLast4 ? ` · •••${r.paymentLast4}` : ''}</Text>
                    <Text style={[styles.attendantName, { color: colors.danger }]}>
                      -${(r.amount / 100).toFixed(2)}
                    </Text>
                  </View>
                  <Text style={styles.attendantLocations} numberOfLines={1}>
                    {r.refundedByName ? `Refunded by ${titleCase(r.refundedByName)}` : 'Refunded by Owner'} · {r.reason || 'No reason'}
                  </Text>
                  <Text style={styles.attendantLocations}>
                    {new Date(r.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </Text>
                  <View style={[styles.statusBadge, { backgroundColor: (r.status === 'succeeded' ? colors.success : r.status === 'pending' ? colors.warning : colors.danger) + '20', alignSelf: 'flex-start', marginTop: 4 }]}>
                    <Text style={[styles.statusText, { color: r.status === 'succeeded' ? colors.success : r.status === 'pending' ? colors.warning : colors.danger }]}>
                      {r.status === 'succeeded' ? 'Refunded' : r.status === 'pending' ? 'Pending' : 'Failed'}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* All Shifts */}
        {attendants.length > 0 && (
          <Card style={styles.shiftsCard}>
            <Text style={styles.sectionTitle}>All Shifts</Text>
            <AdminToolbar
              search={shiftsSearch}
              onSearchChange={setShiftsSearch}
              onSearch={() => fetchShifts(1)}
              total={shiftsTotal}
              page={shiftsPage}
              limit={10}
              onPageChange={(p) => fetchShifts(p)}
              csvEndpoint={`/api/attendants/owner-shifts?sort=${shiftsSort}&order=${shiftsSortOrder}${shiftsSearch ? `&search=${shiftsSearch}` : ''}${shiftsStatusFilter ? `&status=${shiftsStatusFilter}` : ''}`}
              csvFilename="all-shifts.csv"
              label="shift"
              filterOptions={[
                { label: 'Active', value: 'active' },
                { label: 'Completed', value: 'completed' },
                { label: 'Abandoned', value: 'abandoned' },
              ]}
              activeFilter={shiftsStatusFilter}
              onFilterChange={(v) => setShiftsStatusFilter(v)}
            />

            {shiftsLoading ? (
              <ActivityIndicator size="small" color={colors.primary} style={{ paddingVertical: spacing.lg }} />
            ) : shifts.length === 0 ? (
              <Text style={styles.shiftsEmptyText}>No shifts found</Text>
            ) : (
              shifts.map((shift, idx) => (
                <TouchableOpacity
                  key={shift.id}
                  onPress={() => setSelectedShift(shift)}
                  style={[styles.shiftRow, idx < shifts.length - 1 && { borderBottomWidth: 1, borderBottomColor: colors.borderLight }]}
                >
                  <View style={styles.shiftRowHeader}>
                    <Text style={styles.shiftAttendantName} numberOfLines={1}>{titleCase(shift.attendantName)}</Text>
                    <View style={[
                      styles.shiftStatusBadge,
                      shift.status === 'active' ? styles.shiftStatusActive :
                      shift.status === 'completed' ? styles.shiftStatusCompleted : styles.shiftStatusAbandoned
                    ]}>
                      <Text style={[
                        styles.shiftStatusBadgeText,
                        shift.status === 'active' ? styles.shiftStatusActiveText :
                        shift.status === 'completed' ? styles.shiftStatusCompletedText : styles.shiftStatusAbandonedText
                      ]}>{shift.status === 'active' ? 'Active' : shift.status === 'completed' ? 'Done' : 'Left'}</Text>
                    </View>
                  </View>
                  <Text style={styles.shiftMeta} numberOfLines={1}>{shift.locationName}</Text>
                  <Text style={styles.shiftMeta} numberOfLines={1}>
                    {formatDateTime(shift.clockInTime)} → {shift.clockOutTime ? formatDateTime(shift.clockOutTime) : (shift.status === 'active' ? 'now' : '—')}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text style={styles.shiftMeta}>{formatDuration(shift.durationMinutes)}</Text>
                    {shift.selfCorrectedAt && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, backgroundColor: colors.warning + '20' }}>
                        <Ionicons name="flag" size={10} color={colors.warning} />
                        <Text style={{ fontSize: 10, color: colors.warning, fontWeight: '600' }}>Self-corrected</Text>
                      </View>
                    )}
                    {!!shift.notes && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, backgroundColor: colors.primary + '20' }}>
                        <Ionicons name="document-text" size={10} color={colors.primary} />
                        <Text style={{ fontSize: 10, color: colors.primary, fontWeight: '600' }}>Notes</Text>
                      </View>
                    )}
                    {shift.edited && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, backgroundColor: colors.secondary + '20' }}>
                        <Ionicons name="pencil" size={10} color={colors.secondary} />
                        <Text style={{ fontSize: 10, color: colors.secondary, fontWeight: '600' }}>Edited</Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>
              ))
            )}
          </Card>
        )}

        <View style={{ height: spacing.xxl }} />

        {/* Shift Detail Modal */}
        <Modal
          visible={!!selectedShift}
          animationType="slide"
          transparent={true}
          onRequestClose={() => setSelectedShift(null)}
        >
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setSelectedShift(null)}>
            <View style={[styles.modalContent, { paddingBottom: modalBottomPadding + spacing.lg }]} onStartShouldSetResponder={() => true}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Shift Details</Text>
                <TouchableOpacity onPress={() => setSelectedShift(null)}>
                  <Ionicons name="close" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
              {selectedShift && (
                <ScrollView>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Attendant</Text>
                    <Text style={styles.detailValue}>{titleCase(selectedShift.attendantName)}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Location</Text>
                    <Text style={styles.detailValue}>{selectedShift.locationName}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Status</Text>
                    <View style={[
                      styles.shiftStatusBadge,
                      selectedShift.status === 'active' ? styles.shiftStatusActive :
                      selectedShift.status === 'completed' ? styles.shiftStatusCompleted : styles.shiftStatusAbandoned
                    ]}>
                      <Text style={[
                        styles.shiftStatusBadgeText,
                        selectedShift.status === 'active' ? styles.shiftStatusActiveText :
                        selectedShift.status === 'completed' ? styles.shiftStatusCompletedText : styles.shiftStatusAbandonedText
                      ]}>{selectedShift.status === 'active' ? 'Active' : selectedShift.status === 'completed' ? 'Completed' : 'Abandoned'}</Text>
                    </View>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Clock In</Text>
                    <Text style={styles.detailValue}>{formatDateTime(selectedShift.clockInTime)}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Clock Out</Text>
                    <Text style={styles.detailValue}>{selectedShift.clockOutTime ? formatDateTime(selectedShift.clockOutTime) : 'Still active'}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Duration</Text>
                    <Text style={styles.detailValue}>{formatDuration(selectedShift.durationMinutes)}</Text>
                  </View>
                  {selectedShift.notes ? (
                    <View style={[styles.detailRow, { flexDirection: 'column', alignItems: 'flex-start' }]}>
                      <Text style={styles.detailLabel}>Notes</Text>
                      <Text style={[styles.detailValue, { textAlign: 'left', marginLeft: 0, marginTop: spacing.xs }]}>{selectedShift.notes}</Text>
                    </View>
                  ) : null}
                  {selectedShift.lockedAt ? (
                    <View style={styles.detailRow}>
                      <Text style={styles.detailLabel}>Locked</Text>
                      <Text style={styles.detailValue}>{selectedShift.lockedPeriodLabel || 'yes'}</Text>
                    </View>
                  ) : null}
                  <Button
                    title={selectedShift.lockedAt ? 'Edit (period locked)' : 'Edit Timecard'}
                    onPress={() => {
                      setEditingShift({
                        id: selectedShift.id,
                        attendantName: selectedShift.attendantName,
                        locationName: selectedShift.locationName,
                        clockInTime: selectedShift.clockInTime,
                        clockOutTime: selectedShift.clockOutTime || null,
                        notes: selectedShift.notes || null,
                        status: selectedShift.status,
                        lockedAt: selectedShift.lockedAt || null,
                      });
                      setSelectedShift(null);
                    }}
                    icon={<Ionicons name="pencil" size={16} color={colors.white} />}
                    style={{ marginTop: spacing.md }}
                    disabled={!!selectedShift.lockedAt}
                  />
                </ScrollView>
              )}
            </View>
          </TouchableOpacity>
        </Modal>

        <EditShiftModal
          visible={editingShift !== null}
          shift={editingShift}
          onClose={() => setEditingShift(null)}
          onSaved={() => fetchShifts(shiftsPage)}
        />

        </ContentContainer>
      </ScrollView>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.background,
  },
  scrollContent: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  headerButtons: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  emptyTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginTop: spacing.md,
  },
  emptyText: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    textAlign: 'center',
    marginTop: spacing.sm,
    maxWidth: 280,
  },
  emptyButton: {
    marginTop: spacing.lg,
  },
  // Compact attendant rows
  attendantListCard: {
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  attendantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  attendantRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  attendantLeft: {
    flex: 1,
    marginRight: spacing.sm,
  },
  attendantName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  attendantLocations: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginTop: 2,
  },
  attendantRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  statusBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: borderRadius.sm,
  },
  statusActive: {
    backgroundColor: c.success + '20',
  },
  statusInactive: {
    backgroundColor: c.textLight + '20',
  },
  statusText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
  },
  statusTextActive: {
    color: c.success,
  },
  statusTextInactive: {
    color: c.textLight,
  },
  refundHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  refundBadge: {
    backgroundColor: c.danger,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: spacing.md,
  },
  refundBadgeText: {
    color: c.white,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.bold,
  },
  refundRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  // Shifts section
  shiftsCard: {
    marginTop: spacing.md,
  },
  sectionTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.md,
  },
  shiftsEmptyText: {
    fontSize: fontSize.sm,
    color: c.textLight,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  shiftRow: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  shiftRowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  shiftAttendantName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    flex: 1,
    marginRight: spacing.sm,
  },
  shiftMeta: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginTop: 2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: c.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: spacing.lg,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  modalTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  detailLabel: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
  },
  detailValue: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: c.text,
    textAlign: 'right' as const,
    flex: 1,
    marginLeft: spacing.md,
  },
  shiftStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  shiftStatusActive: {
    backgroundColor: c.warning + '20',
  },
  shiftStatusCompleted: {
    backgroundColor: c.success + '20',
  },
  shiftStatusAbandoned: {
    backgroundColor: c.danger + '20',
  },
  shiftStatusBadgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
  },
  shiftStatusActiveText: {
    color: c.warning,
  },
  shiftStatusCompletedText: {
    color: c.success,
  },
  shiftStatusAbandonedText: {
    color: c.danger,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
